import type { PRISMIC_ZONE_LAYOUT } from '@/app/constants/zone';
import type { Brand, Region } from '@/types/constants';
import type { Game, GameTrackingInfo } from '@/types/games';
import type { GameGridLayoutContext, GameViewModel } from '@/types/games/game-view-model';
import { PRODUCT_THROTTLES } from '@/utils/feature-flags/constants';
import { ROUTES } from '@/utils/constants';
import { getServerContext } from '@/store/server-context';
import {
    getGameAnimatedTileUrl,
    getGameName,
    getImageSmallUrl,
    getImageUrl,
    getProviderName,
    getVideoUrl
} from './game-adapter';

export type AdaptGameOptions = {
    /** Tile position in the zone grid — only needed when animated tiles are enabled. */
    gridLayoutCtx?: GameGridLayoutContext;
    disableAnimatedTiles?: boolean;
    /** Pass explicitly on the client; on the server these default from server context. */
    region?: Region;
    isMobile?: boolean;
    animatedTileEnabled?: boolean;
};

type ResolvedAdaptContext = {
    region: Region;
    isMobile: boolean;
    gridLayoutCtx?: GameGridLayoutContext;
    animatedTileEnabled: boolean;
};

const resolveAdaptContext = (options?: AdaptGameOptions): ResolvedAdaptContext => {
    const server = getServerContext();

    const region = options?.region ?? server.region;
    const isMobile = options?.isMobile ?? server.isMobile;

    const animatedTileEnabled =
        options?.animatedTileEnabled ??
        (!options?.disableAnimatedTiles &&
            server.currentRoute === ROUTES.HOMEPAGE &&
            Boolean(server.featureFlags[PRODUCT_THROTTLES.HAS_ANIMATED_TILES]));

    return {
        region,
        isMobile,
        gridLayoutCtx: options?.gridLayoutCtx,
        animatedTileEnabled
    };
};

/** Maps a raw Prismic/API game document to the flat view model consumed by UI components. */
export const adaptGame = (game: Game, options?: AdaptGameOptions): GameViewModel => {
    const ctx = resolveAdaptContext(options);
    const providerName = getProviderName(game.data?.provider?.data?.providername);

    return {
        uid: game.uid,
        name: getGameName(game.data?.display_name),
        providerName,
        description: game.data?.native_description?.[0]?.text ?? '',
        launcherUrlProps: {
            providerName,
            region: ctx.region,
            brand: process.env.BRAND as Brand,
            gameName: getGameName(game.data?.display_name),
            rgi: game.data?.rgi,
            ganId: game.data?.ganid,
            providerRef: ctx.isMobile ? game.data?.rgs_code_mobile : game.data?.rgs_code_desktop,
            gameId: game.id,
            gameUid: game.uid,
            idNative: game.data?.id_native,
            idNativeXsell: game.data?.id_native_xsell
        },
        isPopup: ctx.isMobile,
        logoUrl: getImageSmallUrl(game.data?.logo),
        backgroundUrl: getImageUrl(game.data?.background),
        mergedImageUrl: getImageUrl(game.data?.flattened),
        customBackgroundColor: game.data?.custom_background_color ?? '',
        label: game.data?.label as string | undefined,
        feedsCode: ctx.isMobile ? game.data?.feeds_code_mobile : game.data?.feeds_code_desktop,
        isNew: game.data?.is_new ?? false,
        isSiteWideJackpot: game.data?.isSiteWideJackpot ?? false,
        animatedTileVideoUrl: ctx.animatedTileEnabled
            ? getGameAnimatedTileUrl(game.data?.animated_tiles_assets, ctx.gridLayoutCtx)
            : null,
        videoTrailerUrl: getVideoUrl(game.data?.video_trailer),
        uniquePlayerCount: game.data?.unique_player_count
    };
};

export const adaptGames = (
    games: Game[],
    gridLayout?: PRISMIC_ZONE_LAYOUT,
    options?: Omit<AdaptGameOptions, 'gridLayoutCtx'>
): GameViewModel[] =>
    games.map((game, index) =>
        adaptGame(game, {
            ...options,
            gridLayoutCtx: gridLayout ? { layout: gridLayout, position: index + 1 } : undefined
        })
    );

/** Slim tracking payload for zone impression events over the RSC → client boundary. */
export const adaptGameTrackingInfo = (game: Game): GameTrackingInfo => ({
    uid: game.uid,
    rank_order: (game as Game & { rank_order?: number | string | null }).rank_order ?? null,
    data: {
        ganid: game.data?.ganid,
        display_name: getGameName(game.data?.display_name),
        internal_code: game.data?.internal_code ?? '',
        trigger_game_name: game.data?.trigger_game_name,
        mean_sps_rank: game.data?.mean_sps_rank,
        mean_sps: game.data?.mean_sps,
        model_type: (game.data as typeof game.data & { model_type?: string })?.model_type
    }
});
